import { SkeletonBodyText, SkeletonDisplayText } from "@shopify/polaris";

import { useAdminLoading } from "../utils/adminLoading";

/**
 * The loading picture for /ga4-setup, defined ONCE so the route's gates cannot drift
 * apart: the Suspense fallback in components/App.jsx (chunk downloading) and the
 * page's own `if (fetching)` branch while the analytics_configuration query runs.
 *
 * /ga4-setup had the worst version of the defect described in PageSkeleton.jsx: the
 * Suspense gate drew RouteLoader's large centred spinner, and the query gate drew a
 * bare `<SkeletonPage><s-section><SkeletonBodyText/></s-section></SkeletonPage>` — a
 * HEADINGLESS page. So the title appeared only at the third picture, which is
 * precisely the jump a skeleton exists to remove. That branch is now
 * `return <Ga4SetupSkeleton/>` (routes/ga4-setup.jsx:237-239).
 *
 * ⚠️ FILE NAME: the route imports `../components/Ga4SetupSkeleton`
 * (routes/ga4-setup.jsx:7), so this file must keep exactly that casing — macOS hides
 * a mismatch, a case-sensitive CI box does not.
 *
 * ⚠️ `heading` must stay byte-identical to routes/ga4-setup.jsx's own
 * `<s-page heading="Google Analytics Integration">` (ga4-setup.jsx:243,426). That
 * route sets no `inlineSize`, so this one must not either.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS HAND-ROLLED RATHER THAN A <PageSkeleton>
 * ─────────────────────────────────────────────────────────────────────────────
 * PageSkeleton draws heading → N sections. This page puts TWO things above its first
 * section (ga4-setup.jsx:428-447): a subdued one-line intro paragraph, and a Polaris
 * React `<Tabs>` bar with two tabs ("Analytics Dashboard", "Google Analytics
 * (Optional)"). PageSkeleton's `children` render AFTER the sections, so there is no
 * way to express that order through it — and leaving the tab bar out would let the
 * sections land high and then be shoved down by it, the jump this file prevents.
 *
 * WHAT THE REAL PAGE DRAWS ON FIRST PAINT, and what this mirrors:
 *   1. The subdued intro paragraph (ga4-setup.jsx:429-431). → one body line.
 *   2. The two-tab bar (ga4-setup.jsx:257-268, 444). → two fixed-width blocks sized
 *      roughly to those two labels.
 *   3. Tab 0 is selected by default (`useState(0)`, ga4-setup.jsx:32), so the body is
 *      `renderPostHogDashboard()` (ga4-setup.jsx:270-303) — TWO sections:
 *        a. "Nudgesmart Analytics": a heading + subdued paragraph on one side with an
 *           "Always Enabled" badge opposite, then a success banner.
 *        b. "View Analytics": a section heading and a two-line paragraph.
 *
 * DELIBERATELY NOT DRAWN:
 *   • The `slot="breadcrumb-actions"` Home link (ga4-setup.jsx:427). It is a real
 *     navigation control wired to `useNavigate`; a skeleton must not ship a working
 *     link, and a dead one is worse than the hairline shift its absence costs.
 *   • The banner at ga4-setup.jsx:433-442, which appears only when the URL carries a
 *     `success`/`error` param — data-dependent, so reserving space for it would be
 *     wrong on the common load.
 *   • Everything under tab 1 (the GA4 form), which is unreachable on first paint.
 */
export default function Ga4SetupSkeleton() {
  // Hold Shopify's own header progress bar for as long as this is mounted — the same
  // affordance PageSkeleton takes. Zero-argument and unconditional by design; see
  // utils/adminLoading.js for why it must not be turned into a boolean.
  useAdminLoading();

  return (
    <s-page heading="Google Analytics Integration">
      <s-stack direction="block" gap="large">
        {/* The subdued one-line intro under the page heading. */}
        <SkeletonBodyText lines={1} />

        {/* The two-tab bar. Widths approximate the two real labels so the tabs do not
            visibly resize at handoff. */}
        <s-grid gridTemplateColumns="150px 210px" gap="base" alignItems="center">
          <SkeletonBodyText lines={1} />
          <SkeletonBodyText lines={1} />
        </s-grid>

        {/* Section a — "Nudgesmart Analytics" + badge, then the success banner. */}
        <s-section accessibilityLabel="Loading analytics overview">
          <s-stack direction="block" gap="base">
            <s-grid gridTemplateColumns="1fr auto" gap="base" alignItems="center">
              <s-stack direction="block" gap="small">
                <SkeletonDisplayText size="small" />
                <SkeletonBodyText lines={1} />
              </s-stack>
              <s-box inlineSize="120px">
                <SkeletonBodyText lines={1} />
              </s-box>
            </s-grid>
            <SkeletonBodyText lines={2} />
          </s-stack>
        </s-section>

        {/* Section b — the "View Analytics" heading and its paragraph. */}
        <s-section accessibilityLabel="Loading analytics links">
          <s-stack direction="block" gap="base">
            <SkeletonDisplayText size="small" />
            <SkeletonBodyText lines={2} />
          </s-stack>
        </s-section>
      </s-stack>
    </s-page>
  );
}
